import {
  booleanAttribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  numberAttribute,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  TemplateRef,
  WritableSignal,
  inject,
} from '@angular/core';
import {
  ColumnKeys,
  ExceptionRules,
  ExpandMode,
  FirstRowConfig,
  Item,
  PaginationConfig,
  PlaceholderConfig,
  RowsExceptionRules,
  SelectInputConfig,
  TableColumn,
  TooltipConfig,
  UpdateTableData,
} from './table.utils';
import { SharedService } from '../../shared.service';
import { CabinDTO } from '../../../../api';
import { formatLocaleNumber } from '../../../../utils/common-methods';
import { NzTableComponent, NzThMeasureDirective, NzCellEllipsisDirective } from 'ng-zorro-antd/table';
import { NgClass, NgTemplateOutlet, NgStyle, DecimalPipe, PercentPipe, CurrencyPipe } from '@angular/common';
import { TooltipDirective } from 'ngx-bootstrap/tooltip';
import { FormsModule } from '@angular/forms';
import { NumbersValidationDirective } from '../../directives/numbers-validation.directive';
import { SuffixDirective } from '../../directives/suffix.directive';
import { NzSwitchComponent } from 'ng-zorro-antd/switch';
import { NzSpaceCompactItemDirective } from 'ng-zorro-antd/space';
import { NzSelectComponent, NzOptionComponent } from 'ng-zorro-antd/select';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { TranslatePipe } from '@ngx-translate/core';

let updateDataTriggerCount = 1;

@Component({
    selector: 'dynamica-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    NzTableComponent,
    NgClass,
    NzThMeasureDirective,
    NzCellEllipsisDirective,
    NgTemplateOutlet,
    NgStyle,
    TooltipDirective,
    FormsModule,
    NumbersValidationDirective,
    SuffixDirective,
    NzSwitchComponent,
    NzSpaceCompactItemDirective,
    NzSelectComponent,
    NzOptionComponent,
    NzTooltipDirective,
    DecimalPipe,
    PercentPipe,
    CurrencyPipe,
    TranslatePipe
],
})
export class TableComponent implements OnChanges {
  
  private sharedService = inject(SharedService);
  private cdr = inject(ChangeDetectorRef);
  private zone = inject(NgZone);

  tableData: WritableSignal<any[]> = signal([]);
  @Input({ transform: numberAttribute }) tableHeaderGroupHeight: number = 0;
  @Input() tableHeader!: TemplateRef<any>;
  @Input() showSizeChanger: boolean;
  @Input({ required: true }) data: any[];
  @Input({ required: true }) columns: TableColumn<any>[];
  @Input({ required: true, transform: numberAttribute }) totalCount: number;
  @Input({ required: true, transform: numberAttribute }) pageIndex: number;
  @Input({ required: true, transform: numberAttribute }) pageSize: number;
  @Input({ transform: booleanAttribute }) expandableCollapsibleRows: boolean =
    false;
  @Input() expandCol: ColumnKeys | null = null;
  @Input({ transform: booleanAttribute }) tableHeaderGroupsExist: boolean =
    false;
  @Input() firstRowConfig: FirstRowConfig = null;
  @Input() addRightBorderForColumnCollection: number[] = [];
  @Input() tooltipConfig: TooltipConfig[] = [];
  @Output() tableDataChange = new EventEmitter<UpdateTableData<any>>();
  @Output() cancelOrConfirmTableData = new EventEmitter<
    UpdateTableData<any>
  >();
  @Input() placeholderConfig: PlaceholderConfig[] = [];
  @Input() selectInputConfig: SelectInputConfig[] = [];
  @Output() paginationChange = new EventEmitter<PaginationConfig>();
  @Input() rowsExceptionRules: RowsExceptionRules[];
  @Input() coloredCellsColumnsKey: string[];
  @Input() validateCellsColumnsKey: string[];
  @Input() parentId: string;
  @Input() childId: string;
  @Input() validateCellsRows: number[];
  @Output() rowSelected: EventEmitter<object> = new EventEmitter<object>();
  @Output() sorting: EventEmitter<TableColumn<any>> = new EventEmitter<
    TableColumn<any>
  >();
  @Output() pageSizeChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() linkTypeCellClick: EventEmitter<object> = new EventEmitter<object>();
  @Input() sortKey: ColumnKeys | null = null;
  @Input() isReset: boolean = false;
  stateEnum = CabinDTO.StateEnum;
  isSortReset = false;
  expandedRows: string[] = [];
  expandMode: WritableSignal<ExpandMode> = signal('manual');
  formatNumber = formatLocaleNumber;
  protected disableSelectBehavior = false;

  ngOnChanges(changes: SimpleChanges) {
    const isReset = changes['isReset']?.currentValue === true;
    const dataChanged = !!changes['data'];
    if (isReset) {
      this.expandedRows = [];
      this.expandMode.set('collapsed');
    }
    if (dataChanged) {
      this.tableData.set(
        this.data.map((row) => ({
          ...row,
          collapsed: this.getRowCollapsedState(row[this.parentId]),
        })),
      );
    }
    if (changes['sortKey']) {
      this.sortKey = changes['sortKey'].currentValue;
      this.isSortReset = !this.sortKey;
      this.cdr.markForCheck();
    }
  }

  changePageIndex(value: number): void {
    this.pageIndex = value;
    this.paginationChange.emit({ page: value, size: this.pageSize });
  }

  onSort(column: TableColumn<any>) {
    this.sorting.emit(column);
    this.sortKey = column.id;
  }

  handleTableDataChange(
    rowIdx: number,
    columnName: string,
    value: any,
    subrowIdx: number,
    ignoreTriggerCount = false,
  ) {
    if (updateDataTriggerCount === 1 && !ignoreTriggerCount) {
      this.tableDataChange.emit({
        rowIdx,
        columnName,
        value,
        tableData: this.tableData(),
        subrowIdx,
      });
      --updateDataTriggerCount;
    }

    if (ignoreTriggerCount) {
      this.tableDataChange.emit({
        rowIdx,
        columnName,
        value,
        tableData: this.tableData(),
        subrowIdx,
      });
    }
  }

  handleCancelOrConfirmTableData(
    rowIdx: number,
    columnName: string,
    value: any,
    subrowIdx: number,
  ) {
    if (updateDataTriggerCount === 1) {
      this.cancelOrConfirmTableData.emit({
        rowIdx,
        columnName,
        value,
        subrowIdx,
        tableData: this.tableData(),
      });
      --updateDataTriggerCount;
    }
  }

  onSelectRowHandler(row: object, rowId: string) {
    row[this.parentId] = rowId;
    this.rowSelected.emit(row);
  }

  getRowTooltipConfig(columnId: string): TooltipConfig | null {
    return this.tooltipConfig?.find((t) => t.dataProperty === columnId) ?? null;
  }

  getRowPlaceholderConfig(columnId: string): string | null {
    return (
      this.placeholderConfig?.find((p) => p.dataProperty === columnId)?.text ??
      null
    );
  }

  getRowSelectInputConfig(id: ColumnKeys): Item[] | null {
    return this.selectInputConfig?.find((config) => config.columnId === id)
      ?.options;
  }

  getRowExceptionRules(
    rowIdx: number,
    columnIdx: number,
  ): ExceptionRules | null {
    return this.rowsExceptionRules
      ?.find((config) => config.rowIdx === rowIdx)
      ?.exceptionRules.find((rules) => rules.columnIdx === columnIdx);
  }

  handleOnMouseoverInputField() {
    this.disableSelectBehavior = true;
  }

  handleOnMouseleaveInputField() {
    this.disableSelectBehavior = false;
  }

  resetUpdateDataTriggerCount() {
    updateDataTriggerCount = 1;
  }

  handleOnLinkTypeCellClick(row: object, parentRow) {
    row['productName'] = parentRow['productName'];
    row['duration'] = parentRow['duration'];
    row['productId'] = parentRow['productId'];
    this.linkTypeCellClick.emit(row);
  }

  public toggleExpandAll(): void {
    this.sharedService.showLoader();
    this.cdr.detectChanges();

    setTimeout(() => {
      const currentMode = this.expandMode();

      if (currentMode === 'expanded') {
        this.expandMode.set('manual');
      } else {
        this.expandMode.set('expanded');
        this.expandedRows = []; // Clear manual tracking
      }

      this.tableData.update((data) =>
        data.map((r) => ({
          ...r,
          collapsed: this.getRowCollapsedState(r.productId),
        })),
      );

      this.cdr.detectChanges();
      this.sharedService.hideLoader();
    }, 0);
  }

  public toggleCollapseAll(): void {
    this.sharedService.showLoader();
    this.cdr.detectChanges();

    setTimeout(() => {
      const currentMode = this.expandMode();

      if (currentMode === 'collapsed') {
        this.expandMode.set('manual');
      } else {
        this.expandMode.set('collapsed');
        this.expandedRows = []; // Clear manual tracking
      }
      this.tableData.update((data) =>
        data.map((r) => ({
          ...r,
          collapsed: this.getRowCollapsedState(r.productId),
        })),
      );

      this.cdr.detectChanges();
      this.sharedService.hideLoader();
    }, 0);
  }

  changePageSize(event) {
    this.paginationChange.emit({ page: this.pageIndex, size: event });
  }

  protected tableScrollYCalculationCss() {
    return this.tableHeader
      ? 'calc(100vh - 350px)'
      : `calc(100vh - ${350 - 48}px)`;
  }

  protected getTheNameOfTheItemFromSelectInputList(
    columnId: ColumnKeys,
    value: string,
  ): string {
    return this.getRowSelectInputConfig(columnId)?.find(
      (item) => item.value === value,
    )?.label;
  }

  protected handleExpandCollapseChange(
    event: MouseEvent,
    rowIdx: number,
  ): void {
    event.stopPropagation();

    const currentRow = this.tableData()[rowIdx];
    const parentRowId = currentRow[this.parentId];
    const willBeCollapsed = !currentRow.collapsed;

    // Force manual mode when the user toggles a single row
    if (this.expandMode() !== 'manual') {
      this.expandMode.set('manual');
    }

    // Maintain explicit list of expanded rows for manual mode
    if (willBeCollapsed) {
      // collapsing -> remove from expanded list
      this.expandedRows = this.expandedRows.filter((id) => id !== parentRowId);
    } else {
      // expanding -> ensure in expanded list
      if (!this.expandedRows.includes(parentRowId)) {
        this.expandedRows.push(parentRowId);
      }
    }

    // Update just this row’s collapsed state
    this.tableData.update((data) => {
      const newData = [...data];
      newData[rowIdx] = { ...newData[rowIdx], collapsed: willBeCollapsed };
      return newData;
    });
  }

  private getRowCollapsedState(parentRowId: string): boolean {
    const mode = this.expandMode();
    if (mode === 'expanded') {
      return false;
    }
    if (mode === 'collapsed') {
      return true;
    }
    return !this.expandedRows.includes(parentRowId);
  }
}
